import React, { ReactNode, useState } from 'react'
import { PamError, PamConfirmDialog } from '../components/PamStateComponents'
import { useResource } from '../resource/useResource'
import {
  CrudTable,
  CrudForm,
  CrudSecondaryButton,
  CrudDangerButton,
  CrudActionsRow,
  PageStack,
  PageTitle,
  SectionBlock,
  SectionTitle,
  TenantGateText,
} from '../components/crud'
import type { CrudColumn, CrudField, CrudFieldOption } from '../components/crud'
import type {
  AdminAppItem,
  AdminCreateAppReq,
  AdminUpdateAppReq,
} from '../../../gen/client/react/client'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'

const ROOT_VALUE = '__root__'

const STATUS_OPTIONS = ['active', 'suspended'].map((v) => ({
  value: v,
  label: v,
}))

const columns: CrudColumn<AdminAppItem>[] = [
  { key: 'name', header: 'Name' },
  { key: 'id', header: 'ID' },
  { key: 'parentId', header: 'Parent ID', render: (a) => a.parentId ?? '-' },
  { key: 'status', header: 'Status' },
]

/** Build the "Parent" select options: a root option plus every existing app
 * (excluding the app currently being edited, to prevent an app being its own
 * parent). */
function buildParentOptions(
  apps: AdminAppItem[],
  excludeId?: string
): CrudFieldOption[] {
  return [
    { value: ROOT_VALUE, label: '— root —' },
    ...apps
      .filter((a) => a.id !== excludeId)
      .map((a) => ({ value: a.id, label: a.name })),
  ]
}

function buildFields(parentOptions: CrudFieldOption[]): CrudField[] {
  return [
    { name: 'name', label: 'Name', type: 'text', required: true },
    {
      name: 'parent',
      label: 'Parent',
      type: 'select',
      options: parentOptions,
      required: true,
    },
    { name: 'description', label: 'Description', type: 'text' },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: STATUS_OPTIONS,
      required: true,
    },
    {
      name: 'attributes',
      label: 'Attributes (JSON)',
      type: 'textarea',
    },
  ]
}

const CREATE_INITIAL: Record<string, unknown> = {
  parent: ROOT_VALUE,
  status: 'active',
}

/**
 * Build the CREATE request. Root selected → OMIT parentId entirely (create
 * defaults to a root app when parentId is absent).
 */
const toCreateReq = (
  tenantId: string,
  v: Record<string, unknown>
): AdminCreateAppReq => {
  const parent = String(v.parent ?? ROOT_VALUE)
  return {
    tenantId,
    name: String(v.name ?? ''),
    status: v.status ? String(v.status) : undefined,
    description: v.description ? String(v.description) : undefined,
    attributes: v.attributes ? String(v.attributes) : undefined,
    ...(parent !== ROOT_VALUE ? { parentId: parent } : {}),
  }
}

/**
 * Build the UPDATE request. parentId/description/etc are nullable tri-state:
 * omitting the field means "leave unchanged"; an explicit empty string means
 * "clear / set to root". Root selected on EDIT → explicit parentId: "" so the
 * app is re-parented to root (omitting here would incorrectly mean "leave
 * unchanged").
 */
const toUpdateReq = (
  tenantId: string,
  appId: string,
  v: Record<string, unknown>
): AdminUpdateAppReq => {
  const parent = String(v.parent ?? ROOT_VALUE)
  return {
    tenantId,
    appId,
    name: v.name ? String(v.name) : undefined,
    status: v.status ? String(v.status) : undefined,
    description:
      v.description !== undefined ? String(v.description) : undefined,
    attributes: v.attributes !== undefined ? String(v.attributes) : undefined,
    parentId: parent === ROOT_VALUE ? '' : parent,
  }
}

export const Apps = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()
  const [editing, setEditing] = useState<AdminAppItem | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<AdminAppItem | null>(null)

  const rs = useResource<AdminAppItem>({
    list: async () => {
      const resp = await client.adminListApps({ tenantId: tenant })
      return resp.apps ?? []
    },
    remove: async (id) => {
      await client.adminDeleteApp({ tenantId: tenant, appId: id })
    },
  })

  const parentOptions = buildParentOptions(rs.items, editing?.id)
  const fields = buildFields(parentOptions)

  const handleCreate = async (v: Record<string, unknown>): Promise<void> => {
    try {
      await client.adminCreateApp(toCreateReq(tenant, v))
      await rs.reload()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Create failed')
    }
  }

  const handleEdit = async (v: Record<string, unknown>): Promise<void> => {
    if (!editing) return
    try {
      await client.adminUpdateApp(toUpdateReq(tenant, editing.id, v))
      await rs.reload()
      setEditing(null)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Update failed')
    }
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return
    setActionError(null)
    await rs.remove(pendingDelete.id)
    setPendingDelete(null)
  }

  const rowActions = (a: AdminAppItem): ReactNode => (
    <CrudActionsRow>
      <CrudSecondaryButton
        type="button"
        onClick={() => {
          setEditing(a)
          setActionError(null)
        }}
      >
        Edit
      </CrudSecondaryButton>
      <CrudDangerButton
        type="button"
        onClick={() => {
          setActionError(null)
          setPendingDelete(a)
        }}
      >
        Delete
      </CrudDangerButton>
    </CrudActionsRow>
  )

  if (tenant === 'system') {
    return (
      <PageStack>
        <TenantGateText>Please select a tenant to manage apps.</TenantGateText>
      </PageStack>
    )
  }

  return (
    <PageStack>
      <PageTitle>Apps</PageTitle>

      {(rs.error || actionError) && (
        <PamError message={rs.error ?? actionError ?? ''} />
      )}

      <SectionBlock>
        <SectionTitle>All Apps</SectionTitle>
        <CrudTable
          columns={columns}
          items={rs.items}
          rowActions={rowActions}
          loading={rs.loading}
          caption="Apps list"
          emptyMessage="No apps configured yet."
        />
      </SectionBlock>

      {editing && (
        <SectionBlock>
          <SectionTitle>Edit App</SectionTitle>
          <CrudForm
            key={editing.id}
            fields={fields}
            initial={{
              name: editing.name,
              parent: editing.parentId ?? ROOT_VALUE,
              description: editing.description ?? '',
              status: editing.status,
              attributes: editing.attributes ?? '',
            }}
            onSubmit={handleEdit}
            submitLabel="Save Changes"
          />
          <CrudSecondaryButton
            type="button"
            onClick={() => {
              setEditing(null)
              setActionError(null)
            }}
          >
            Cancel
          </CrudSecondaryButton>
        </SectionBlock>
      )}

      <SectionBlock>
        <SectionTitle>Create App</SectionTitle>
        <CrudForm
          fields={fields}
          initial={CREATE_INITIAL}
          onSubmit={handleCreate}
          submitLabel="Create App"
        />
      </SectionBlock>

      <PamConfirmDialog
        open={pendingDelete !== null}
        title="Delete app"
        body={`Delete app "${pendingDelete?.name ?? ''}"? This cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
        color="error"
      />
    </PageStack>
  )
}
